/**
 * Glossary - Glossário de Termos do Mercado da Borracha
 * 
 * Baseado no Tripé:
 * ⚖️ JUSTIÇA - Termos de fontes oficiais (CNA, IEA, APABOR)
 * 🔒 SEGURANÇA - Informações verificáveis
 * ✨ SIMPLICIDADE - Fácil de entender e buscar
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useTranslation } from '../../translations';

const Glossary = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const terms = [
    // PRODUTO
    {
      term: 'GEB-10',
      aliases: ['geb 10', 'geb10', 'granulado escuro'],
      definition: t('glossaryGEB10') || 'Granulado Escuro Brasileiro, classe 10. Borracha beneficiada composta por 100% de borracha natural proveniente de coágulos de látex. É o principal produto comercializado no Brasil e serve como referência de preço.',
      category: 'product',
      importance: 'high',
    },
    {
      term: t('coagulum') || 'Coágulo',
      aliases: ['coagulo', 'cernambi'],
      definition: t('glossaryCoagulum') || 'Látex coagulado (solidificado) através da adição de ácido. É a forma como a maioria dos produtores vende sua produção para as usinas beneficiadoras.',
      category: 'product',
      importance: 'high',
    },
    {
      term: 'Látex',
      aliases: ['latex', 'leite da seringueira'],
      definition: 'Líquido branco extraído da seringueira, composto por água, borracha e outras substâncias. Contém entre 30-40% de sólidos (borracha seca).',
      category: 'product',
      importance: 'medium',
    },
    {
      term: 'TSR-20',
      aliases: ['tsr20', 'tsr 20', 'borracha especificada'],
      definition: 'Technical Specified Rubber - borracha tecnicamente especificada com até 20% de sujeira. Padrão internacional usado como referência para cotações na Bolsa de Singapura.',
      category: 'product',
      importance: 'medium',
    },
    {
      term: 'SMR-20',
      aliases: ['smr20', 'smr 20'],
      definition: 'Standard Malaysian Rubber - borracha padrão da Malásia. Referência internacional de preço, cotada diariamente.',
      category: 'product',
      importance: 'medium',
    },

    // QUALIDADE
    {
      term: 'DRC (' + (t('drc') || 'Teor de Borracha Seca') + ')',
      aliases: ['drc', 'tbs', 'teor de borracha', 'dry rubber content', 'teor borracha seca'],
      definition: t('glossaryDRC') || 'Porcentagem de borracha seca presente no coágulo. A referência padrão é 53%. Quanto maior o DRC, maior o valor por kg. Exemplo: DRC 60% vale mais que DRC 53%.',
      category: 'quality',
      importance: 'high',
    },
    {
      term: 'TBS',
      aliases: ['tbs', 'teor borracha'],
      definition: 'Teor de Borracha Seca - mesmo que DRC. Termo em português usado para medir a qualidade do coágulo.',
      category: 'quality',
      importance: 'high',
    },

    // PREÇO
    {
      term: t('importPrice') || 'Preço de Importação',
      aliases: ['preco importacao', 'preço importação', 'preco de importacao', 'iea preço'],
      definition: 'Preço de referência calculado pelo IEA/CNA. Inclui: preço internacional + câmbio + frete + seguro + impostos. É a BASE para cálculo do preço do coágulo.',
      category: 'price',
      importance: 'high',
    },
    {
      term: t('participationRate') || 'Taxa de Participação',
      aliases: ['taxa participacao', 'percentual', 'participação'],
      definition: t('glossaryParticipationRate') || 'Percentual do preço de referência (GEB-10 ajustado pelo DRC) repassado ao produtor. Varia de ~50% (crise) a ~85% (mercado favorável). Fatores: oferta/demanda regional, poder de barganha, importações, contratos vs. spot.',
      category: 'price',
      importance: 'high',
    },
    {
      term: t('coagulumProducer') || 'Preço do Coágulo',
      aliases: ['preco produtor', 'preço coágulo', 'preço na origem'],
      definition: 'Valor transacionado na ponta da cadeia (coágulo). Calculado pela fórmula: Preço de Referência × DRC × Taxa de Participação.',
      category: 'price',
      importance: 'high',
    },
    {
      term: 'Bolsa de Singapura (SGX)',
      aliases: ['sgx', 'singapura', 'bolsa singapura', 'cotação internacional'],
      definition: 'Principal referência mundial para cotação de borracha natural. Os preços brasileiros são influenciados por esta cotação.',
      category: 'price',
      importance: 'medium',
    },
    {
      term: 'Preço de Paridade de Importação',
      aliases: ['paridade importação', 'ppi'],
      definition: 'Preço da borracha importada já internalizado no Brasil (com todos os custos). Serve como teto de referência para o preço interno.',
      category: 'price',
      importance: 'medium',
    },

    // PRODUÇÃO
    {
      term: 'Sangria',
      aliases: ['corte', 'extração latex'],
      definition: 'Técnica de extração do látex da seringueira, realizada através de cortes diagonais na casca da árvore. Requer habilidade técnica.',
      category: 'production',
      importance: 'medium',
    },
    {
      term: t('tapper') || 'Sangrador',
      aliases: ['seringueiro', 'trabalhador'],
      definition: 'Profissional que realiza a sangria (extração do látex). Pode representar até 75% dos custos de produção. Mão de obra especializada e escassa.',
      category: 'production',
      importance: 'medium',
    },
    {
      term: 'Heveicultura',
      aliases: ['cultivo seringueira', 'plantio borracha'],
      definition: 'Cultivo da seringueira (Hevea brasiliensis) para produção de borracha natural.',
      category: 'production',
      importance: 'low',
    },
    {
      term: 'Heveicultor',
      aliases: ['produtor borracha', 'seringueiro'],
      definition: 'Produtor rural dedicado ao cultivo de seringueiras para produção de borracha.',
      category: 'production',
      importance: 'low',
    },
    {
      term: 'Seringal',
      aliases: ['plantação', 'área plantada'],
      definition: 'Área plantada com seringueiras para produção de látex/borracha.',
      category: 'production',
      importance: 'low',
    },

    // CADEIA
    {
      term: 'COE (Custo Operacional Efetivo)',
      aliases: ['coe', 'custo efetivo', 'custo operacional'],
      definition: 'Gastos diretos do dia a dia: mão de obra do sangrador (~40%), transporte, insumos, combustível, materiais. Se o preço fica abaixo do COE (~R$ 5,00/kg, estimativa CONAB — veja o valor vigente na aba 📊 Análise), o produtor tem prejuízo em cada kg.',
      category: 'price',
      importance: 'high',
    },
    {
      term: 'COT (Custo Operacional Total)',
      aliases: ['cot', 'custo total', 'custo completo'],
      definition: 'COE + custos invisíveis: depreciação de equipamentos/seringal e pró-labore (salário do proprietário). Para lucro real, o preço precisa superar o COT (~R$ 6,07/kg, estimativa CONAB — veja o valor vigente na aba 📊 Análise).',
      category: 'price',
      importance: 'high',
    },
    {
      term: 'PGPM (Preço Mínimo Garantido)',
      aliases: ['pgpm', 'preço mínimo', 'garantia governo'],
      definition: 'Política do governo que estabelece um preço mínimo via CONAB (R$ 4,87/kg para coágulo DRC 53%, safra jul/26–jun/27 — veja o valor vigente na aba Cotações). Quando o mercado paga menos, o produtor pode solicitar complementação.',
      category: 'price',
      importance: 'high',
    },
    {
      term: 'Wintering (Troca de Folhas)',
      aliases: ['wintering', 'troca de folhas', 'refolhamento'],
      definition: 'Período entre fevereiro e abril em que as seringueiras trocam de folhas e reduzem a produção de látex. Na Ásia, reduz a oferta global e tende a elevar preços.',
      category: 'production',
      importance: 'medium',
    },
    {
      term: 'Beneficiadora / Usina',
      aliases: ['usina', 'beneficiadora', 'industria'],
      definition: 'Indústria que compra o coágulo do produtor e transforma em borracha beneficiada (GEB-10). Faz a ponte entre produtor e indústria de transformação.',
      category: 'chain',
      importance: 'medium',
    },
    {
      term: 'Cadeia Produtiva',
      aliases: ['cadeia', 'fluxo produção'],
      definition: 'Sequência: Produtor (coágulo) → Beneficiadora (GEB-10) → Indústria (pneus, luvas, etc.) → Consumidor final.',
      category: 'chain',
      importance: 'medium',
    },

    // INSTITUIÇÃO
    {
      term: 'IEA',
      aliases: ['instituto economia agricola', 'iea sp'],
      definition: 'Instituto de Economia Agrícola, vinculado à Secretaria de Agricultura de São Paulo. Calcula mensalmente o Preço de Referência de Importação.',
      category: 'institution',
      importance: 'high',
    },
    {
      term: 'APABOR',
      aliases: ['associação paulista', 'apabor sp'],
      definition: 'Associação Paulista de Produtores e Beneficiadores de Borracha. Divulga o indicador de referência de preços do GEB-10.',
      category: 'institution',
      importance: 'high',
    },
    {
      term: 'CNA',
      aliases: ['confederação agricultura', 'cna brasil'],
      definition: 'Confederação da Agricultura e Pecuária do Brasil. Realiza estudos sobre o mercado da borracha natural.',
      category: 'institution',
      importance: 'medium',
    },
    {
      term: 'CONAB',
      aliases: ['companhia abastecimento'],
      definition: 'Companhia Nacional de Abastecimento. Calcula e divulga o custo de produção da borracha natural por região.',
      category: 'institution',
      importance: 'medium',
    },

    // IMPOSTO
    {
      term: 'Imposto de Importação (II)',
      aliases: ['ii', 'imposto importacao', 'tarifa importação'],
      definition: 'Taxa cobrada sobre borracha importada. Atualmente em 10,8% (prorrogado até 2027). Influencia o preço de paridade de importação.',
      category: 'tax',
      importance: 'medium',
    },
    {
      term: 'TEC (Tarifa Externa Comum)',
      aliases: ['tec', 'tarifa mercosul'],
      definition: 'Alíquota de importação aplicada no Mercosul. Já foi 14% (2016-2017), depois 3,2%, e agora voltou para 10,8%.',
      category: 'tax',
      importance: 'low',
    },
    {
      term: 'CIF',
      aliases: ['cif', 'custo seguro frete'],
      definition: 'Cost, Insurance and Freight. Preço do produto incluindo custo, seguro e frete até o porto de destino. Base para cálculo de impostos.',
      category: 'tax',
      importance: 'low',
    },
  ];

  // Map categories with translations
  const categoryLabels = {
    all: t('allCategories'),
    product: t('categoryProduct'),
    quality: t('categoryQuality'),
    production: t('categoryProduction'),
    chain: t('categoryChain'),
    price: t('categoryPrice'),
    institution: t('categoryInstitution'),
    tax: t('categoryTax'),
  };

  const categories = ['all', 'product', 'quality', 'production', 'chain', 'price', 'institution', 'tax'];

  // Função de busca melhorada - busca no termo, aliases e definição
  const normalizeText = (text) => {
    return text.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/[-_]/g, ' '); // Substitui hífen e underscore por espaço
  };

  const filteredTerms = terms.filter(item => {
    // Filtro por categoria
    if (selectedCategory !== 'all' && item.category !== selectedCategory) {
      return false;
    }

    // Se não há termo de busca, mostrar todos
    if (!searchTerm.trim()) {
      return true;
    }

    const normalizedSearch = normalizeText(searchTerm);
    
    // Busca no termo principal
    if (normalizeText(item.term).includes(normalizedSearch)) {
      return true;
    }
    
    // Busca nos aliases
    if (item.aliases && item.aliases.some(alias => normalizeText(alias).includes(normalizedSearch))) {
      return true;
    }
    
    // Busca na definição
    if (normalizeText(item.definition).includes(normalizedSearch)) {
      return true;
    }
    
    return false;
  });

  // Ordenar por importância
  const sortedTerms = filteredTerms.sort((a, b) => {
    const order = { high: 0, medium: 1, low: 2 };
    return (order[a.importance] || 2) - (order[b.importance] || 2);
  });

  const getCategoryColor = (category) => {
    const colors = {
      'product': 'bg-green-100 text-green-800 border-green-300',
      'quality': 'bg-blue-100 text-blue-800 border-blue-300',
      'production': 'bg-amber-100 text-amber-800 border-amber-300',
      'chain': 'bg-purple-100 text-purple-800 border-purple-300',
      'price': 'bg-red-100 text-red-800 border-red-300',
      'institution': 'bg-gray-100 text-gray-800 border-gray-300',
      'tax': 'bg-orange-100 text-orange-800 border-orange-300',
    };
    return colors[category] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getImportanceIndicator = (importance) => {
    if (importance === 'high') return '⭐';
    return '';
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-green-800 flex items-center gap-2">
            <span className="text-2xl">📖</span>
            {t('rubberMarketGlossary')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-4">
            {t('understandTechnicalTerms')} {t('starTermsImportant')}
          </p>

          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              placeholder={`🔍 ${t('searchTermPlaceholder')}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === cat 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {categoryLabels[cat]}
              </button>
            ))}
          </div>

          {/* Results count */}
          <div className="text-sm text-gray-500 mb-4">
            {sortedTerms.length} {sortedTerms.length !== 1 ? t('termsFound').replace('{count}', sortedTerms.length) : t('termsFound').replace('{count}', sortedTerms.length)}
            {searchTerm && ` ${t('forSearch').replace('{term}', searchTerm)}`}
          </div>

          {/* Terms List */}
          <div className="space-y-3">
            {sortedTerms.map((item, index) => (
              <div 
                key={index} 
                className={`p-4 rounded-lg border-2 ${
                  item.importance === 'high' 
                    ? 'bg-green-50 border-green-300' 
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <h4 className="font-bold text-gray-800 flex items-center gap-1">
                    {getImportanceIndicator(item.importance)} {item.term}
                  </h4>
                  <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap border ${getCategoryColor(item.category)}`}>
                    {categoryLabels[item.category]}
                  </span>
                </div>
                <p className="text-gray-600 text-sm mt-2">{item.definition}</p>
              </div>
            ))}
          </div>

          {sortedTerms.length === 0 && (
            <div className="text-center py-8">
              <span className="text-4xl">🔍</span>
              <p className="text-gray-500 mt-2">{t('noTermsFound').replace('{term}', searchTerm)}</p>
              <p className="text-gray-400 text-sm mt-1">{t('tryOtherWord')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dica */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-4">
          <div className="flex items-start gap-2">
            <span className="text-xl">💡</span>
            <div>
              <p className="text-blue-800 font-medium">{t('tip')}</p>
              <p className="text-blue-700 text-sm">
                {t('glossaryTip')} {t('startWithTerms')} <strong>GEB-10</strong>, <strong>DRC</strong>, <strong>{t('importPrice')}</strong> {t('and')} <strong>{t('participationRate')}</strong>.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Glossary;
